import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Pencil, Car, RefreshCw, X, Save, AlertCircle, Plus, Trash2,
  Users, Briefcase, Eye, EyeOff, Star, ToggleLeft, ToggleRight, DollarSign, GripVertical
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminVehiclesPage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [reordering, setReordering] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  
  // Form data
  const [formData, setFormData] = useState({
    vehicle_id: "",
    type: "",
    subtitle: "",
    passengers: "",
    luggage: "",
    base_price: "",
    price_multiplier: "",
    price_scales: [],
    image: "",
    is_active: true,
    show_on_homepage: true,
    is_popular: false,
    is_best_price: false
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem("adminToken");
    setLoading(true);
    setError("");
    
    try {
      const res = await fetch(`${API_BASE}/vehicles/all`, {
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!res.ok) {
        if (res.status === 401) {
          localStorage.removeItem("adminToken");
          navigate("/admin/login");
          return;
        }
        throw new Error("Failed to fetch vehicles");
      }

      const data = await res.json();
      setVehicles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingVehicle(null);
    setIsCreating(true);
    setFormData({
      vehicle_id: "",
      type: "",
      subtitle: "",
      passengers: "",
      luggage: "",
      base_price: "",
      price_multiplier: "1.0",
      price_scales: [],
      image: "",
      is_active: true,
      show_on_homepage: true,
      is_popular: false,
      is_best_price: false
    });
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (vehicle) => {
    setEditingVehicle(vehicle);
    setIsCreating(false);
    setFormError("");
    setFormData({
      vehicle_id: vehicle.vehicle_id || "",
      type: vehicle.type || "",
      subtitle: vehicle.subtitle || "",
      passengers: vehicle.passengers || "",
      luggage: vehicle.luggage || "",
      base_price: vehicle.base_price || "",
      price_multiplier: vehicle.price_multiplier || "1.0",
      price_scales: vehicle.price_scales || [],
      image: vehicle.image || "",
      is_active: vehicle.is_active !== false,
      show_on_homepage: vehicle.show_on_homepage !== false,
      is_popular: vehicle.is_popular === true,
      is_best_price: vehicle.is_best_price === true
    });
    setShowModal(true);
  };

  // Price scales management
  const addPriceScale = () => {
    const scales = [...formData.price_scales];
    const lastScale = scales[scales.length - 1];
    const newFromKm = lastScale ? (lastScale.to_km || 0) + 1 : 0;
    scales.push({ from_km: newFromKm, to_km: null, multiplier: 1.0 });
    setFormData({ ...formData, price_scales: scales });
  };

  const updatePriceScale = (index, field, value) => {
    const scales = [...formData.price_scales];
    if (field === 'from_km' || field === 'to_km') {
      scales[index][field] = value === '' || value === null ? null : parseInt(value);
    } else if (field === 'multiplier') {
      scales[index][field] = parseFloat(value) || 1.0;
    }
    setFormData({ ...formData, price_scales: scales });
  };

  const removePriceScale = (index) => {
    const scales = [...formData.price_scales];
    scales.splice(index, 1);
    setFormData({ ...formData, price_scales: scales });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("adminToken");
    setSaving(true);
    setFormError("");

    try {
      let payload;
      let url;
      let method;

      // Clean price_scales - remove empty entries and validate
      const cleanedScales = formData.price_scales.filter(s => 
        s.multiplier && (s.from_km !== null || s.from_km === 0)
      );

      if (isCreating) {
        // Creating new vehicle
        if (!formData.vehicle_id || !formData.type) {
          throw new Error("Vehicle ID and Type are required");
        }
        payload = {
          vehicle_id: formData.vehicle_id,
          type: formData.type,
          subtitle: formData.subtitle || "Premium vehicle",
          passengers: parseInt(formData.passengers) || 4,
          luggage: parseInt(formData.luggage) || 3,
          has_ac: true,
          base_price: parseInt(formData.base_price) || 100,
          price_multiplier: parseFloat(formData.price_multiplier) || 1.0,
          price_scales: cleanedScales,
          image: formData.image || null,
          description: null,
          is_active: Boolean(formData.is_active),
          show_on_homepage: Boolean(formData.show_on_homepage),
          is_popular: Boolean(formData.is_popular),
          is_best_price: Boolean(formData.is_best_price)
        };
        url = `${API_BASE}/vehicles`;
        method = "POST";
      } else {
        // Updating existing vehicle - explicitly send boolean values
        payload = {
          type: formData.type || null,
          subtitle: formData.subtitle || null,
          passengers: formData.passengers ? parseInt(formData.passengers) : null,
          luggage: formData.luggage ? parseInt(formData.luggage) : null,
          base_price: formData.base_price ? parseInt(formData.base_price) : null,
          price_multiplier: formData.price_multiplier ? parseFloat(formData.price_multiplier) : null,
          price_scales: cleanedScales,
          image: formData.image || null,
          is_active: Boolean(formData.is_active),
          show_on_homepage: Boolean(formData.show_on_homepage),
          is_popular: Boolean(formData.is_popular),
          is_best_price: Boolean(formData.is_best_price)
        };
        url = `${API_BASE}/vehicles/${editingVehicle.vehicle_id}`;
        method = "PATCH";
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to save vehicle");
      }

      await fetchData();
      setShowModal(false);
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm("Are you sure you want to delete this vehicle?")) return;
    
    const token = localStorage.getItem("adminToken");
    try {
      const res = await fetch(`${API_BASE}/vehicles/${vehicleId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to delete vehicle");
      }

      await fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    // Reorder locally first for instant feedback
    const newVehicles = [...vehicles];
    const [draggedItem] = newVehicles.splice(draggedIndex, 1);
    newVehicles.splice(dropIndex, 0, draggedItem);
    setVehicles(newVehicles);
    setDraggedIndex(null);

    // Save new order to backend
    const token = localStorage.getItem("adminToken");
    setReordering(true);
    try {
      const res = await fetch(`${API_BASE}/vehicles/reorder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          vehicle_ids: newVehicles.map(v => v.vehicle_id)
        })
      });

      if (!res.ok) {
        throw new Error("Failed to save order");
      }
    } catch (err) {
      // Revert on error
      await fetchData();
      alert("Failed to save vehicle order");
    } finally {
      setReordering(false);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-vehicles">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Vehicles</h1>
            <p className="text-slate-500">Manage transfer vehicles</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={openCreateModal} data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Vehicle
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Vehicles Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {reordering && (
            <div className="bg-blue-50 px-4 py-2 text-sm text-blue-700 border-b border-blue-100">
              Saving new order...
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="vehicles-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="w-10 px-2 py-3"></th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Vehicle</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Capacity</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Pricing</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Homepage</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Loading vehicles...
                    </td>
                  </tr>
                ) : vehicles.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                      No vehicles found
                    </td>
                  </tr>
                ) : (
                  vehicles.map((vehicle, index) => (
                    <tr 
                      key={vehicle.vehicle_id} 
                      className={`hover:bg-slate-50 ${draggedIndex === index ? 'opacity-50 bg-slate-100' : ''}`}
                      data-testid={`vehicle-row-${vehicle.vehicle_id}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <td className="px-2 py-4 cursor-grab active:cursor-grabbing">
                        <GripVertical className="w-5 h-5 text-slate-400" />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center">
                            {vehicle.image ? (
                              <img src={vehicle.image} alt={vehicle.type} className="w-full h-full object-cover" />
                            ) : (
                              <Car className="w-6 h-6 text-slate-400" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                              {vehicle.type}
                              {vehicle.is_popular && (
                                <Star className="w-4 h-4 text-teal-500 fill-teal-500" />
                              )}
                            </div>
                            <div className="text-xs text-slate-500">{vehicle.subtitle}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-slate-600">
                            <Users className="w-4 h-4 text-slate-400" />
                            {vehicle.passengers}
                          </span>
                          <span className="flex items-center gap-1 text-slate-600">
                            <Briefcase className="w-4 h-4 text-slate-400" />
                            {vehicle.luggage}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-lg font-bold text-slate-900">€{vehicle.base_price}</div>
                        <div className="text-xs text-slate-500">×{vehicle.price_multiplier || 1.0} multiplier</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {vehicle.show_on_homepage ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <Eye className="w-3 h-3" />
                              Visible
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                              <EyeOff className="w-3 h-3" />
                              Hidden
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          vehicle.is_active 
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}>
                          {vehicle.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditModal(vehicle)}
                            data-testid={`edit-btn-${vehicle.vehicle_id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(vehicle.vehicle_id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-btn-${vehicle.vehicle_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="vehicle-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold">
                  {isCreating ? "Add New Vehicle" : "Edit Vehicle"}
                </h3>
                {!isCreating && editingVehicle && (
                  <p className="text-sm text-slate-500">{editingVehicle.vehicle_id}</p>
                )}
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {formError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {formError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {isCreating && (
                <div className="space-y-2">
                  <Label htmlFor="vehicle_id">Vehicle ID *</Label>
                  <Input
                    id="vehicle_id"
                    value={formData.vehicle_id}
                    onChange={(e) => setFormData({...formData, vehicle_id: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                    placeholder="luxury-sedan"
                    required
                    data-testid="input-vehicle-id"
                  />
                  <p className="text-xs text-slate-500">Unique identifier (lowercase, no spaces)</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type Name *</Label>
                  <Input
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    placeholder="Standard Car"
                    required
                    data-testid="input-type"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subtitle">Subtitle</Label>
                  <Input
                    id="subtitle"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                    placeholder="Mercedes E-Class or similar"
                    data-testid="input-subtitle"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="image">Image URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="image"
                    value={formData.image}
                    onChange={(e) => setFormData({...formData, image: e.target.value})}
                    placeholder="https://images.unsplash.com/..."
                    className="flex-1"
                    data-testid="input-image"
                  />
                  {formData.image && (
                    <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                      <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500">This image will display on the homepage fleet section</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="passengers">Passengers</Label>
                  <Input
                    id="passengers"
                    type="number"
                    value={formData.passengers}
                    onChange={(e) => setFormData({...formData, passengers: e.target.value})}
                    placeholder="4"
                    data-testid="input-passengers"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="luggage">Luggage</Label>
                  <Input
                    id="luggage"
                    type="number"
                    value={formData.luggage}
                    onChange={(e) => setFormData({...formData, luggage: e.target.value})}
                    placeholder="3"
                    data-testid="input-luggage"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="base_price">Base Price (€)</Label>
                <Input
                  id="base_price"
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                  placeholder="85"
                  data-testid="input-base-price"
                />
                <p className="text-xs text-slate-500">Minimum price floor and homepage "From €XX" display</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="price_multiplier">Legacy Multiplier (Fallback)</Label>
                <Input
                  id="price_multiplier"
                  type="number"
                  step="0.01"
                  value={formData.price_multiplier}
                  onChange={(e) => setFormData({...formData, price_multiplier: e.target.value})}
                  placeholder="1.0"
                  data-testid="input-price-multiplier"
                />
                <p className="text-xs text-slate-500">Used only if no price scales are defined below.</p>
              </div>

              {/* Price Scales Section */}
              <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Price Scales (Distance-Based Pricing)
                  </h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addPriceScale}
                    className="h-7 text-xs"
                    data-testid="add-scale-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Scale
                  </Button>
                </div>
                
                {formData.price_scales.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">No scales defined. Legacy multiplier will be used.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-4 gap-2 text-xs font-medium text-slate-600 px-1">
                      <span>From (km)</span>
                      <span>To (km)</span>
                      <span>Multiplier</span>
                      <span></span>
                    </div>
                    {formData.price_scales.map((scale, index) => (
                      <div key={index} className="grid grid-cols-4 gap-2 items-center" data-testid={`scale-row-${index}`}>
                        <Input
                          type="number"
                          min="0"
                          value={scale.from_km ?? ''}
                          onChange={(e) => updatePriceScale(index, 'from_km', e.target.value)}
                          placeholder="0"
                          className="h-8 text-sm"
                          data-testid={`scale-from-${index}`}
                        />
                        <Input
                          type="number"
                          min="0"
                          value={scale.to_km ?? ''}
                          onChange={(e) => updatePriceScale(index, 'to_km', e.target.value)}
                          placeholder="∞"
                          className="h-8 text-sm"
                          data-testid={`scale-to-${index}`}
                        />
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          value={scale.multiplier}
                          onChange={(e) => updatePriceScale(index, 'multiplier', e.target.value)}
                          placeholder="1.0"
                          className="h-8 text-sm"
                          data-testid={`scale-multiplier-${index}`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePriceScale(index)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`scale-delete-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 mt-2">
                      Example: 70km route with scales [0-40: ×2] [41+: ×1.5] = (40×2) + (30×1.5) = €125
                    </p>
                  </div>
                )}
              </div>

              {/* Homepage Options */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Homepage Display
                </h4>
                
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-slate-700">Show on Homepage</span>
                    <p className="text-xs text-slate-500">Display in the fleet section</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, show_on_homepage: !formData.show_on_homepage})}
                    className={`flex items-center gap-2 ${formData.show_on_homepage ? "text-green-600" : "text-slate-400"}`}
                    data-testid="toggle-homepage"
                  >
                    {formData.show_on_homepage ? (
                      <ToggleRight className="w-8 h-8" />
                    ) : (
                      <ToggleLeft className="w-8 h-8" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-slate-700 flex items-center gap-1">
                      <Star className="w-3 h-3 text-teal-500" />
                      Most Popular Badge
                    </span>
                    <p className="text-xs text-slate-500">Show "MOST POPULAR" badge</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, is_popular: !formData.is_popular})}
                    className={`flex items-center gap-2 ${formData.is_popular ? "text-teal-500" : "text-slate-400"}`}
                    data-testid="toggle-popular"
                  >
                    {formData.is_popular ? (
                      <ToggleRight className="w-8 h-8" />
                    ) : (
                      <ToggleLeft className="w-8 h-8" />
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-slate-700 flex items-center gap-1">
                      <DollarSign className="w-3 h-3 text-amber-500" />
                      Best Price Badge
                    </span>
                    <p className="text-xs text-slate-500">Show "BEST PRICE" badge</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, is_best_price: !formData.is_best_price})}
                    className={`flex items-center gap-2 ${formData.is_best_price ? "text-amber-500" : "text-slate-400"}`}
                    data-testid="toggle-best-price"
                  >
                    {formData.is_best_price ? (
                      <ToggleRight className="w-8 h-8" />
                    ) : (
                      <ToggleLeft className="w-8 h-8" />
                    )}
                  </button>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">Vehicle Active</span>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, is_active: !formData.is_active})}
                  className={`flex items-center gap-2 ${formData.is_active ? "text-green-600" : "text-slate-400"}`}
                  data-testid="toggle-active"
                >
                  {formData.is_active ? (
                    <ToggleRight className="w-8 h-8" />
                  ) : (
                    <ToggleLeft className="w-8 h-8" />
                  )}
                </button>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-vehicle-btn">
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {isCreating ? "Create Vehicle" : "Update Vehicle"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
